export type AccessResourceType =
  | 'folder'
  | 'document'
  | 'clause'
  | 'clause_variant'
  | 'clause_root'
  | 'playbook';

export type AccessPrincipalType = 'user' | 'department' | 'everyone';

export type AccessRole = 'edit' | 'view' | 'view_download' | 'use';

export type AccessGrantSource = 'inherited' | 'direct' | 'department';

export const EVERYONE_PRINCIPAL_ID = 'everyone';

export type AccessPrincipal = {
  type: AccessPrincipalType;
  id: string;
  name: string;
  email?: string;
};

export type AccessPrincipalSearchResult = {
  principal: AccessPrincipal;
  subtitle: string;
  memberCount?: number;
};

export type AccessGrant = {
  principal: AccessPrincipal;
  role: AccessRole;
  source: AccessGrantSource;
  sourceName?: string;
  isOwner?: boolean;
  readOnly?: boolean;
};

export type AccessResourceRef = {
  type: AccessResourceType;
  id: string;
  name: string;
  breadcrumb?: string[];
  /** Root folders have no parent to inherit from, so they never show the toggle. */
  isRootFolder?: boolean;
};

export const clauseRootResource: AccessResourceRef = {
  type: 'clause_root',
  id: 'clause_root',
  name: 'Clause Library',
};

/** Diff submitted to `updateResourceAccess` — only what changed. */
export type AccessGrantDiff = {
  inheritFromParent: boolean;
  addedGrants: AccessGrant[];
  updatedGrants: AccessGrant[];
  removedPrincipals: AccessPrincipal[];
};

export type BulkAccessScope = 'folders' | 'documents' | 'both';

export type BulkAccessMode = 'add' | 'override';

export type BulkAccessCopiesChoice = 'apply' | 'skip';

export type BulkAccessTarget = {
  id: string;
  type: 'folder' | 'document';
  name: string;
};

/** Clause Library bulk selection item before mapping to `AccessResourceRef`. */
export type ClauseBulkSelectionItem = Readonly<{
  clauseId?: string;
  key: string;
  name: string;
  type: Extract<AccessResourceType, 'clause' | 'clause_variant'>;
}>;

/** The selection that the bulk Manage Access modal is open for. */
export type BulkAccessSelection = {
  folderId?: string;
  selectAll: boolean;
  targets: BulkAccessTarget[];
  totalCount?: number;
};

export type BulkAccessPreview = {
  folderCount: number;
  documentCount: number;
  mixedStateCount: number;
  copiesCount: number;
};

export type BulkAccessItemResult = {
  id: string;
  type: 'folder' | 'document';
  name: string;
  priorGrantsRemoved?: number;
  accessSummary?: string;
};

export type BulkAccessResult = {
  mode: BulkAccessMode;
  scope: BulkAccessScope;
  updatedCount: number;
  items: BulkAccessItemResult[];
  grantsApplied: AccessGrant[];
  locationLabel?: string;
};

export type BulkAccessRequest = {
  targets: BulkAccessTarget[];
  selectAll?: boolean;
  folderId?: string;
  scope: BulkAccessScope;
  mode: BulkAccessMode;
  copiesChoice: BulkAccessCopiesChoice;
  grants: AccessGrant[];
};

export type BulkAccessPreviewRequest = {
  targets: BulkAccessTarget[];
  selectAll?: boolean;
  folderId?: string;
};

/** Clause / variation target for clause bulk Manage Access. */
export type ClauseBulkAccessTarget = {
  id: string;
  type: Extract<AccessResourceType, 'clause' | 'clause_variant'>;
  name: string;
};

export type ClauseBulkApplyTo = 'clause' | 'variation' | 'clause_and_variation';

export type ClauseBulkAccessRequest = {
  targets: ClauseBulkAccessTarget[];
  applyTo: ClauseBulkApplyTo;
  mode: BulkAccessMode;
  grants: AccessGrant[];
};

export type ClauseBulkAccessItemResult = {
  id: string;
  type: Extract<AccessResourceType, 'clause' | 'clause_variant'>;
  name: string;
  accessSummary?: string;
};

export type ClauseBulkAccessResult = {
  mode: BulkAccessMode;
  applyTo: ClauseBulkApplyTo;
  updatedCount: number;
  items: ClauseBulkAccessItemResult[];
  grantsApplied: AccessGrant[];
  jobId?: string;
};
